import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { KeyRound, LogOut } from "lucide-react";

export default function ChangePassword() {
    const { user, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const forced = !!user?.must_change_password;

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (pwd.length < 4) { setError("A senha deve ter ao menos 4 caracteres."); return; }
        if (pwd !== pwd2) { setError("As senhas não conferem."); return; }
        setSaving(true);
        try {
            await api.post("/auth/change-password", { new_password: pwd });
            await refreshUser();
            navigate("/", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || "Falha ao trocar senha");
        } finally {
            setSaving(false);
        }
    }

    async function doLogout() {
        await logout();
        navigate("/login", { replace: true });
    }

    return (
        <div className="min-h-screen w-full relative overflow-hidden bg-white flex items-center justify-center px-6">
            <div className="pointer-events-none absolute -top-44 -right-44 w-[680px] h-[680px] opacity-[0.06] rotate-12 select-none">
                <img src="/logo-licito.png" alt="" className="w-full h-full object-contain" />
            </div>
            <div className="pointer-events-none absolute inset-0" style={{
                background: "radial-gradient(900px 600px at 18% 22%, rgba(45, 212, 191, 0.10), transparent 60%), radial-gradient(900px 600px at 82% 78%, rgba(34, 197, 94, 0.10), transparent 60%)"
            }} />

            <div className="relative z-10 w-full max-w-md" data-testid="change-password-page">
                <div className="flex flex-col items-center mb-8">
                    <img src="/logo-licito.png" alt="licito.guru" className="h-16 w-auto mb-4" />
                    <div className="h-12 w-12 rounded-full bg-[#0F2044] text-white flex items-center justify-center mb-3">
                        <KeyRound className="h-5 w-5" />
                    </div>
                    <h1 className="font-display font-bold text-2xl text-[#0F2044]">
                        {forced ? "Defina sua nova senha" : "Trocar senha"}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1 text-center">
                        {forced ? "Este é seu primeiro acesso. Escolha uma nova senha para continuar." : "Atualize sua senha de acesso."}
                    </p>
                </div>

                <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-[0_8px_30px_rgba(15,32,68,0.06)] p-7 space-y-4" data-testid="change-password-form">
                    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                        Usuário: <span className="font-semibold text-[#0F2044]">{user?.email}</span>
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-slate-700 mb-1.5 uppercase tracking-[0.12em]">Nova senha</label>
                        <input
                            type="password"
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                            required
                            data-testid="new-password-input"
                            className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#0F2044] focus:ring-2 focus:ring-[#0F2044]/15 focus:outline-none text-sm"
                            placeholder="••••••••"
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-slate-700 mb-1.5 uppercase tracking-[0.12em]">Confirmar nova senha</label>
                        <input
                            type="password"
                            value={pwd2}
                            onChange={(e) => setPwd2(e.target.value)}
                            required
                            data-testid="new-password-confirm-input"
                            className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#0F2044] focus:ring-2 focus:ring-[#0F2044]/15 focus:outline-none text-sm"
                            placeholder="••••••••"
                        />
                    </div>
                    {error && (
                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="change-password-error">{error}</div>
                    )}
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="change-password-submit"
                        className="w-full bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold py-2.5 rounded-md disabled:opacity-60"
                    >
                        {saving ? "Salvando…" : "Salvar nova senha"}
                    </button>
                    <button
                        type="button"
                        onClick={doLogout}
                        data-testid="change-password-logout"
                        className="w-full text-xs text-slate-500 hover:text-[#0F2044] inline-flex items-center justify-center gap-1.5 py-2"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Sair sem trocar
                    </button>
                </form>
            </div>
        </div>
    );
}
